<template>
  <div class="data-grid">
    <!-- Search and filter bar -->
    <div class="row mb-3">
      <div class="col-md-12">
        <div class="d-flex justify-content-between align-items-center gap-2">
          <!-- Left side: Custom filters slot -->
          <div class="d-flex gap-2 flex-grow-0">
            <slot name="filters"></slot>
          </div>
          
          <!-- Right side: Refresh and Search -->
          <div class="d-flex gap-2">
            <button 
              v-if="showRefresh"
              class="btn btn-outline-secondary btn-sm"
              type="button"
              @click="$emit('refresh')"
              :disabled="loading"
              :title="$t('common.refresh') || 'Refresh'"
              style="border-color: #ced4da; border-width: 1px;"
            >
              <i class="ph-duotone ph-arrow-clockwise" :class="{ 'spin': loading }" style="font-size: 0.875rem;"></i>
            </button>
            <div class="input-group input-group-sm" style="max-width: 250px;">
              <span class="input-group-text"><i class="ti ti-search"></i></span>
              <input 
                type="text" 
                class="form-control"
                :placeholder="searchPlaceholder || $t('common.search.inRequests')" 
                v-model="localSearchText"
                @keyup.enter="handleSearch"
                @blur="handleSearch"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error message -->
    <div v-if="error" class="alert alert-danger" role="alert">
      {{ error }}
      <button type="button" class="btn-close float-end" @click="$emit('update:error', null)"></button>
    </div>
    
    <!-- Loading indicator -->
    <div v-if="loading" class="text-center my-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    
    <!-- Empty state message -->
    <div v-if="!loading && !error && filteredData.length === 0" class="alert alert-info">
      {{ emptyStateMessage || 'No data found.' }}
    </div>

    <!-- Table -->
    <div v-if="!loading && !error && filteredData.length > 0" class="table-responsive">
      <table class="table table-hover resizable-table">
        <caption class="visually-hidden">
          {{ tableCaption || $t('common.dataTableCaption') || 'Data table listing records' }}
        </caption>
        <thead>
          <tr>
            <th 
              v-for="(column, index) in columns" 
              :key="column.key" 
              :style="{ width: columnWidths[index] }" 
              class="position-relative"
              :class="{ 'sortable': column.sortable, 'sort-active': column.sortable && sortField === column.sortKey }"
              @click="column.sortable && handleSort(column.sortKey || column.key)"
            >
              <div class="d-flex align-items-center justify-content-between">
                <span>{{ column.label }}</span>
                <span v-if="column.sortable" class="sort-icons ms-2">
                  <i 
                    class="ph-duotone ph-caret-up sort-icon sort-up" 
                    :class="{ 'sort-active': sortField === (column.sortKey || column.key) && sortDirection === 'asc' }"
                  ></i>
                  <i 
                    class="ph-duotone ph-caret-down sort-icon sort-down" 
                    :class="{ 'sort-active': sortField === (column.sortKey || column.key) && sortDirection === 'desc' }"
                  ></i>
                </span>
              </div>
              <div 
                class="column-resizer" 
                @mousedown.prevent="startResize($event, index)"
              ></div>
            </th>
            <th v-if="hasActions" :style="{ width: columnWidths[columns.length] }" class="text-center position-relative">
              {{ $t('common.actionsLabel') || 'Actions' }}
              <div 
                class="column-resizer" 
                @mousedown.prevent="startResize($event, columns.length)"
              ></div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginatedData" :key="getItemKey(item)" @dblclick="handleRowDoubleClick(item)" :class="item._rowClass" style="cursor: pointer;">
            <td v-for="column in columns" :key="column.key">
              <div v-if="column.type === 'badge'" class="cell-content" :class="{ 'badge-with-subtitle': item[column.key + 'Subtitle'] }">
                <span 
                  :class="getBadgeClass(item[column.key], column.badgeMap)"
                  :style="item[column.key + 'Color'] ? { backgroundColor: item[column.key + 'Color'], color: getContrastTextColor(item[column.key + 'Color']) } : null"
                >{{ item[column.key] }}</span>
                <small v-if="item[column.key + 'Subtitle']" class="text-muted text-truncate d-block mt-1" :title="item[column.key + 'Subtitle']">{{ item[column.key + 'Subtitle'] }}</small>
              </div>
              <div v-else-if="column.type === 'icon'" class="cell-content">
                <i v-if="item[column.key]" :class="column.iconClass"></i>
              </div>
              <div v-else-if="column.type === 'truncate'" class="cell-content text-truncate" :title="item[column.key]">
                <div v-if="item[column.key + 'Subtitle']">
                  <div class="fw-bold text-truncate">{{ item[column.key] }}</div>
                  <small class="text-muted text-truncate d-block">{{ item[column.key + 'Subtitle'] }}</small>
                </div>
                <div v-else class="text-truncate">{{ item[column.key] }}</div>
              </div>
              <div v-else-if="column.key === 'buyer'" class="cell-content fw-bold text-truncate" :title="item[column.key]">
                {{ item[column.key] }}
              </div>
              <div v-else-if="item[column.key + 'Subtitle']" class="cell-content">
                <div class="text-truncate" :title="item[column.key]">{{ item[column.key] }}</div>
                <small class="text-muted text-truncate d-block" :title="item[column.key + 'Subtitle']">{{ item[column.key + 'Subtitle'] }}</small>
              </div>
              <div v-else class="cell-content text-truncate" :title="item[column.key]">
                {{ item[column.key] }}
              </div>
            </td>
            <td v-if="hasActions" class="text-center">
              <!-- Check if custom actions slot is provided -->
              <slot name="actions" :item="item">
                <!-- Default actions rendering -->
                <ul class="list-inline mb-0">
                  <li v-if="actions.view || actions.edit" class="list-inline-item">
                    <a href="#" @click.prevent="$emit('view', item)" v-if="item.status === 'Quotation Submitted' || item.status === 'Declined'" class="btn btn-icon btn-sm btn-outline-primary">
                      <i class="ph-duotone ph-eye"></i>
                    </a>
                    <a href="#" @click.prevent="$emit('edit', item)" v-else class="btn btn-icon btn-sm btn-outline-success">
                      <i class="ph-duotone ph-pencil-simple"></i>
                    </a>
                  </li>
                  <li v-for="(action, index) in getVisibleActions(item)" :key="index" class="list-inline-item">
                    <a 
                      href="#" 
                      @click.prevent="handleActionClick(action, item)" 
                      :class="[
                        action.buttonClass || 'btn btn-icon btn-sm btn-outline-secondary',
                        { 'disabled': action.loading?.(item) }
                      ]"
                      :title="action.title || action.event"
                    >
                      <span v-if="action.loading?.(item)" class="spinner-border spinner-border-sm"></span>
                      <i v-else :class="action.icon"></i>
                    </a>
                  </li>
                </ul>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
      <!-- Pagination -->
      <div v-if="paginatedData.length > 0" class="d-flex justify-content-between align-items-center mt-3">
        <div class="d-flex align-items-center">
          <span class="me-3">
            {{ $t('common.pagination.showing') || 'Showing' }} 
            {{ Math.min((localCurrentPage - 1) * localPageSize + 1, getTotalItemsCount()) }}-{{ Math.min(localCurrentPage * localPageSize, getTotalItemsCount()) }} 
            {{ $t('common.pagination.of') || 'of' }} 
            {{ getTotalItemsCount() }} 
            {{ $t('common.pagination.items') || 'items' }}
          </span>
          <div class="d-flex align-items-center">
            <span class="me-2">{{ $t('common.pagination.show') || 'Show' }}</span>
            <select class="form-select form-select-sm" v-model="localPageSize" @change="onPageSizeChange">
              <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
            </select>
          </div>
        </div>
        <nav aria-label="Page navigation">
          <ul class="pagination mb-0">
            <li class="page-item" :class="{ disabled: localCurrentPage === 1 }">
              <a class="page-link" @click.prevent="changePage(localCurrentPage - 1)" aria-label="Previous">
                {{ $t('common.pagination.prev') || 'Previous' }}
              </a>
            </li>
            
            <!-- First Page -->
            <li v-if="totalPages > 0" class="page-item" :class="{ active: localCurrentPage === 1 }">
              <a class="page-link" @click.prevent="changePage(1)">1</a>
            </li>
            
            <!-- Ellipsis for pages before current group -->
            <li v-if="localCurrentPage > 3" class="page-item disabled">
              <span class="page-link">...</span>
            </li>
            
            <!-- Pages around current page (2 before and 2 after) -->
            <template v-for="page in getVisiblePages()" :key="page">
              <li v-if="page > 1 && page < totalPages" class="page-item" :class="{ active: page === localCurrentPage }">
                <a class="page-link" @click.prevent="changePage(page)">{{ page }}</a>
              </li>
            </template>
            
            <!-- Ellipsis for pages after current group -->
            <li v-if="localCurrentPage < totalPages - 2" class="page-item disabled">
              <span class="page-link">...</span>
            </li>
            
            <!-- Last Page -->
            <li v-if="totalPages > 1" class="page-item" :class="{ active: localCurrentPage === totalPages }">
              <a class="page-link" @click.prevent="changePage(totalPages)">{{ totalPages }}</a>
            </li>
            
            <li class="page-item" :class="{ disabled: localCurrentPage >= totalPages }">
              <a class="page-link" @click.prevent="changePage(localCurrentPage + 1)" aria-label="Next">
                {{ $t('common.pagination.next') || 'Next' }}
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useColumnResize } from '@/composables/useColumnResize';

export default {
  name: 'DataGrid',
  props: {
    // Data props
    data: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    itemKey: {
      type: String,
      default: 'id'
    },
    
    // Pagination props
    currentPage: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 10
    },
    pageSizeOptions: {
      type: Array,
      default: () => [5, 10, 25, 50, 100]
    },
    totalItems: {
      type: Number,
      default: 0
    },
    
    // Search props
    searchText: {
      type: String,
      default: ''
    },
    searchPlaceholder: {
      type: String,
      default: ''
    },
    
    // Action props
    actions: {
      type: Object,
      default: () => ({
        view: true,
        edit: true
      })
    },
    customActions: {
      type: Array,
      default: () => []
    },
    
    // State props
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    
    // UI props
    emptyStateMessage: {
      type: String,
      default: ''
    },
    tableCaption: {
      type: String,
      default: ''
    },
    showRefresh: {
      type: Boolean,
      default: false
    },
    
    // Column resizing
    storageKey: {
      type: String,
      default: 'dataGrid_columnWidths'
    },
    
    // Sorting props
    sortField: {
      type: String,
      default: ''
    },
    sortDirection: {
      type: String,
      default: 'asc', // 'asc' or 'desc'
      validator: (value) => ['asc', 'desc'].includes(value)
    }
  },
  emits: [
    'update:currentPage', 
    'update:pageSize', 
    'update:searchText',
    'update:error',
    'update:sortField',
    'update:sortDirection',
    'search', 
    'view', 
    'edit',
    'pageSizeChange',
    'pageChange',
    'sort'
  ],
  setup(props, { emit }) {
    const localCurrentPage = ref(props.currentPage);
    const localPageSize = ref(props.pageSize);
    const localSearchText = ref(props.searchText);
    const sortField = ref(props.sortField);
    const sortDirection = ref(props.sortDirection);
    
    // Check if actions column is needed
    const hasActions = computed(() => {
      return props.actions.view || props.actions.edit || (props.customActions && props.customActions.length > 0);
    });
    
    // Create columns array including actions column if needed
    const columnsWithActions = computed(() => {
      const cols = [...props.columns];
      if (hasActions.value) {
        cols.push({ width: '150px', key: 'actions', label: 'Actions' });
      }
      return cols;
    });
    
    // Initialize column resizing with composable
    const { columnWidths, startResize } = useColumnResize(columnsWithActions.value, props.storageKey);
    
    // Watch for prop changes
    watch(() => props.currentPage, (newVal) => {
      localCurrentPage.value = newVal;
    });
    
    watch(() => props.pageSize, (newVal) => {
      if (newVal !== localPageSize.value) {
        localPageSize.value = newVal;
      }
    });
    
    watch(() => props.searchText, (newVal) => {
      localSearchText.value = newVal;
    });
    
    watch(() => props.sortField, (newVal) => {
      sortField.value = newVal;
    });
    
    watch(() => props.sortDirection, (newVal) => {
      sortDirection.value = newVal;
    });
    
    // Watch for changes in the search text
    watch(localSearchText, (newVal) => {
      // Only update the parent's searchText value, but don't trigger search
      emit('update:searchText', newVal);
      
      // Remove the debounced search to prevent automatic API calls
      // Only the handleSearch method will trigger the search
    });
    
    // Computed properties
    const filteredData = computed(() => {
      // Client-side filtering - can be disabled if server-side filtering is used
      if (!localSearchText.value) return props.data;
      
      const searchTerm = localSearchText.value.toLowerCase();
      return props.data.filter(item => {
        return props.columns.some(column => {
          if (!item[column.key]) return false;
          return String(item[column.key]).toLowerCase().includes(searchTerm);
        });
      });
    });
    
    const totalItems = computed(() => filteredData.value.length);
    
    const totalPages = computed(() => {
      // API'den gelen toplam öğe sayısı varsa onu kullan
      if (props.totalItems > 0) {
        return Math.max(1, Math.ceil(props.totalItems / localPageSize.value));
      }
      
      // API'den toplam sayı gelmemişse client-side hesaplama yap
      return Math.max(1, Math.ceil(totalItems.value / localPageSize.value));
    });
    
    const paginatedData = computed(() => {
      // API-based pagination: data doğrudan API'den gelen tek sayfalık veriyi içerir
      // Bu durumda doğrudan data'yı göster
      if (props.totalItems > 0) {
        return props.data;
      }
      
      // Client-side pagination (API'dan gelmiyorsa)
      const start = (localCurrentPage.value - 1) * localPageSize.value;
      const end = start + localPageSize.value;
      return filteredData.value.slice(start, end);
    });
    
    // Methods
    const handleSearch = () => {
      localCurrentPage.value = 1;
      emit('update:currentPage', 1);
      emit('update:searchText', localSearchText.value);
      emit('search', localSearchText.value);
    };
    
    const onPageSizeChange = () => {
      // Update the page size model and emit the update event
      emit('update:pageSize', localPageSize.value);
      emit('pageSizeChange');
    };
    
    const getVisiblePages = () => {
      if (totalPages.value <= 1) {
        return [];
      }
      
      // If total pages is 5 or less, show all pages
      if (totalPages.value <= 5) {
        const pages = Array.from({ length: totalPages.value - 1 }, (_, i) => i + 2);
        return pages;
      }
      
      // Show 2 pages before and 2 pages after current page
      let start = Math.max(2, localCurrentPage.value - 2);
      let end = Math.min(totalPages.value - 1, localCurrentPage.value + 2);
      
      // Adjust range to always show 5 pages when possible
      if (end - start + 1 < 5) {
        if (start === 2) {
          end = Math.min(totalPages.value - 1, start + 4);
        } else if (end === totalPages.value - 1) {
          start = Math.max(2, end - 4);
        }
      }
      
      return Array.from({ length: end - start + 1 }, (_, i) => i + start);
    };
    
    const changePage = (page) => {
      if (page < 1 || page > totalPages.value) return;
      
      localCurrentPage.value = page;
      emit('update:currentPage', page);
      emit('pageChange', page);
    };
    
    const getItemKey = (item) => {
      return item[props.itemKey];
    };
    
    const getBadgeClass = (value, badgeMap) => {
      if (badgeMap?.[value]) {
        return `badge ${badgeMap[value]}`;
      }
      
      const normalizedValue = String(value ?? '').toLowerCase();
      if (!normalizedValue) {
        return 'badge bg-light-secondary';
      }
      
      if (normalizedValue.includes('success') || normalizedValue.includes('submitted') || normalizedValue.includes('approved')) {
        return 'badge bg-light-success';
      } else if (normalizedValue.includes('warning') || normalizedValue.includes('waiting') || normalizedValue.includes('pending')) {
        return 'badge bg-light-warning';
      } else if (normalizedValue.includes('danger') || normalizedValue.includes('error') || normalizedValue.includes('failed') || normalizedValue.includes('revision')) {
        return 'badge bg-light-danger';
      }
      
      return 'badge bg-light-secondary';
    };

    const getContrastTextColor = (bg) => {
      if (!bg) return '#212529'
      let hex = bg.trim()
      if (hex.startsWith('rgb')) {
        try {
          const m = hex.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
          if (m) {
            const r = parseInt(m[1], 10), g = parseInt(m[2], 10), b = parseInt(m[3], 10)
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b)
            return luminance > 186 ? '#212529' : '#fff'
          }
        } catch {}
      }
      if (hex[0] === '#') hex = hex.slice(1)
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('')
      }
      const r = parseInt(hex.substring(0, 2), 16)
      const g = parseInt(hex.substring(2, 4), 16)
      const b = parseInt(hex.substring(4, 6), 16)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b)
      return luminance > 186 ? '#212529' : '#fff'
    };
    
    const handleRowDoubleClick = (item) => {
      // Double-click should trigger edit action, but only if edit is available
      // Check if the item can be edited (not submitted or declined)
      if (item.status === 'Quotation Submitted' || item.status === 'Declined') {
        // If it's submitted or declined, trigger view instead
        emit('view', item);
      } else {
        // Otherwise, trigger edit
        emit('edit', item);
      }
    };
    
    const getTotalItemsCount = () => {
      // API'den gelen toplam öğe sayısı varsa onu kullan
      if (props.totalItems > 0) {
        return props.totalItems;
      }
      // API'den toplam sayı gelmemişse client-side hesaplama yap
      return totalItems.value;
    };
    
    const handleSort = (field) => {
      // If clicking on the same field, toggle direction
      if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
      } else {
        // If clicking on a new field, set to ascending
        sortField.value = field;
        sortDirection.value = 'asc';
      }
      
      // Reset to first page when sorting
      localCurrentPage.value = 1;
      emit('update:currentPage', 1);
      emit('update:sortField', sortField.value);
      emit('update:sortDirection', sortDirection.value);
      emit('sort', { field: sortField.value, direction: sortDirection.value });
    };
    
    const getVisibleActions = (item) => {
      if (!props.customActions || !Array.isArray(props.customActions)) {
        return [];
      }
      
      return props.customActions.filter(action => {
        // If action has a condition function, check it
        if (typeof action.condition === 'function') {
          const result = action.condition(item);
          return result;
        }
        // If no condition, always show
        return true;
      });
    };
    
    const handleActionClick = (action, item) => {
      // Don't trigger if loading
      if (action.loading?.(item)) {
        return;
      }
      emit(action.event, item);
    };
    
    return {
      localCurrentPage,
      localPageSize,
      localSearchText,
      hasActions,
      filteredData,
      paginatedData,
      totalPages,
      handleSearch,
      onPageSizeChange,
      changePage,
      getItemKey,
      getBadgeClass,
      getContrastTextColor,
      handleRowDoubleClick,
      getVisiblePages,
      getTotalItemsCount,
      handleSort,
      sortField,
      sortDirection,
      columnWidths,
      startResize,
      getVisibleActions,
      handleActionClick
    };
  }
};
</script>

<style scoped>
/* Ensure tables maintain their cell widths */
.table-responsive {
  overflow-x: auto;
  overflow-y: visible;
}

.table {
  table-layout: fixed;
  width: 100%;
}

.table th {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.table td {
  overflow: hidden;
  vertical-align: middle;
}

/* Allow actions column to show dropdowns */
.table tbody {
  overflow: visible;
}

.table tr {
  overflow: visible;
}

.table td:last-child {
  overflow: visible;
  position: relative;
}

.cell-content {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

/* Allow badge content to display normally without truncation */
.cell-content .badge {
  white-space: normal;
  display: inline-block;
}

/* Badge with subtitle should be block to allow subtitle below */
.cell-content.badge-with-subtitle {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.cell-content.badge-with-subtitle .badge {
  display: block;
  width: fit-content;
}

/* Ensure truncate type columns work properly */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resizable-table thead {
  overflow: visible;
}

.resizable-table th {
  position: relative;
  overflow: hidden;
}

.column-resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  user-select: none;
  z-index: 1;
}

.column-resizer:hover,
.column-resizer:active {
  background-color: #0d6efd;
}

/* Add global style for cursor when resizing */
:global(.resizing-columns) {
  cursor: col-resize !important;
  user-select: none !important;
}

/* Add a subtle indicator for resizable columns */
.resizable-table th:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

/* Make the resize handle more visible on hover */
.resizable-table th:hover .column-resizer {
  background-color: rgba(13, 110, 253, 0.3);
}

/* Sorting styles */
.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.sort-icons {
  display: flex;
  flex-direction: column;
  gap: 2px;
  opacity: 0.3;
  transition: opacity 0.2s;
}

.sortable:hover .sort-icons {
  opacity: 0.6;
}

.sort-icon {
  font-size: 0.75rem;
  line-height: 1;
}

.sort-icon.sort-active {
  opacity: 1;
  color: #0d6efd;
}

.sort-active .sort-icons {
  opacity: 1;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style> 